import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, LayoutGrid, Home, Swords, Snowflake, MapPin, Package } from "lucide-react";
import { api } from "../lib/api";
import { Loading, Empty } from "./common";

const glass = "rounded-2xl border border-white/10 bg-[#0a0f1f]/70 backdrop-blur-xl";
const NEUTRAL = "#94a3b8";
const colorOf = (t) => t.crew?.color || NEUTRAL;

export function useTerritoryMap(poll = 20000) {
  const [d, setD] = useState(null);
  const load = () => api.get("/vstreets/map").then(({ data }) => setD(data)).catch(() => setD({ territories: [], crews: [], map: null }));
  useEffect(() => { load(); const t = setInterval(load, poll); return () => clearInterval(t); }, []); // eslint-disable-line
  return [d, load];
}

export function CityMapCanvas({ map, territories, selected, onSelect, onPick, pick, maxVh = 68 }) {
  const ref = useRef(null);
  const w = map.max_x - map.min_x, h = map.max_y - map.min_y;
  const click = (e) => {
    if (!onPick) return;
    const r = ref.current.getBoundingClientRect();
    onPick({ x: Math.round(map.min_x + ((e.clientX - r.left) / r.width) * w), y: Math.round(map.max_y - ((e.clientY - r.top) / r.height) * h) });
  };
  const k = w / 400;
  return (
    <div className="relative w-full mx-auto rounded-xl overflow-hidden border border-white/10 bg-[#060a16]" style={{ aspectRatio: `${w} / ${h}`, width: `min(100%, calc(${maxVh}vh * ${w / h}))` }}>
      <svg ref={ref} viewBox={`${map.min_x} ${-map.max_y} ${w} ${h}`} className={`w-full h-full ${onPick ? "cursor-crosshair" : ""}`} onClick={click} data-testid="vs-map-svg">
        <image href={map.image_url} x={map.min_x} y={-map.max_y} width={w} height={h} preserveAspectRatio="none" />
        {territories.map((t) => t.center && (
          <g key={t.id} onClick={(e) => { e.stopPropagation(); onSelect?.(t); }} className="cursor-pointer" data-testid={`vs-map-zone-${t.id}`}>
            <circle cx={t.center.x} cy={-t.center.y} r={Math.max(t.radius, 6 * k)} fill={colorOf(t)} fillOpacity={selected === t.id ? 0.55 : 0.32}
              stroke={t.contested ? "#f43f5e" : colorOf(t)} strokeWidth={(t.contested ? 3 : selected === t.id ? 2.5 : 1.2) * k}
              strokeDasharray={t.frozen ? `${6 * k} ${4 * k}` : undefined} className={t.contested ? "animate-pulse" : ""} />
            {t.hq && <rect x={t.hq.x - 5 * k} y={-t.hq.y - 5 * k} width={10 * k} height={10 * k} rx={2 * k} fill="#facc15" stroke="#000" strokeWidth={k} />}
            {t.stash && <circle cx={t.stash.x} cy={-t.stash.y} r={4 * k} fill="#fff" stroke="#000" strokeWidth={k} />}
          </g>))}
        {pick && <circle cx={pick.x} cy={-pick.y} r={pick.radius || 6 * k} fill="none" stroke="#facc15" strokeWidth={2 * k} strokeDasharray={`${4 * k} ${3 * k}`} />}
      </svg>
    </div>
  );
}

function BlockGrid({ territories, selected, onSelect }) {
  const groups = useMemo(() => territories.reduce((a, t) => { (a[t.district || "Unzoned"] ||= []).push(t); return a; }, {}), [territories]);
  return (
    <div className="space-y-5" data-testid="vs-grid">
      {Object.entries(groups).map(([district, list]) => (
        <div key={district}>
          <div className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground mb-2">{district}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {list.map((t) => (
              <button key={t.id} onClick={() => onSelect(t)} data-testid={`vs-grid-block-${t.id}`}
                className={`relative text-left rounded-xl p-3 min-h-[86px] border transition-[transform,border-color] duration-200 hover:-translate-y-0.5 ${selected === t.id ? "border-white/60" : "border-white/10"} ${t.contested ? "ring-2 ring-rose-500/70 animate-pulse" : ""}`}
                style={{ background: `linear-gradient(160deg, ${colorOf(t)}55, ${colorOf(t)}14)` }}>
                <div className="font-display font-semibold text-sm leading-tight pr-5">{t.name}</div>
                <div className="text-[11px] text-white/70 mt-1 truncate">{t.crew?.name || "Unclaimed"}</div>
                <div className="absolute top-2 right-2 flex gap-1 text-white/80">
                  {t.contested && <Swords size={12} className="text-rose-400" />}{t.frozen && <Snowflake size={12} />}{t.hq && <Home size={12} className="text-yellow-300" />}
                </div>
              </button>))}
          </div>
        </div>))}
    </div>
  );
}

function Detail({ t }) {
  if (!t) return <p className="text-xs text-muted-foreground">Tap a block to see who holds it.</p>;
  return (
    <div className="space-y-2 text-sm" data-testid="vs-map-detail">
      <div className="font-display font-bold text-lg">{t.name}</div>
      <div className="text-[11px] text-muted-foreground">{t.district || "Unzoned"} · radius {t.radius}m</div>
      <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: colorOf(t) }} />
        {t.crew ? <Link to={`/vstreets/crew/${t.crew.id}`} className="font-semibold hover:text-gold" data-testid="vs-map-detail-crew">{t.crew.name}</Link> : <span className="text-muted-foreground">Unclaimed</span>}</div>
      {t.contested && <div className="text-rose-400 text-xs font-semibold flex items-center gap-1" data-testid="vs-map-detail-contested"><Swords size={12} />Turf war{t.attacker ? ` vs ${t.attacker.name}` : ""}</div>}
      {t.frozen && <div className="text-sky-300 text-xs flex items-center gap-1"><Snowflake size={12} />Frozen by city admins</div>}
      {t.hq && <div className="text-xs flex items-center gap-1 text-yellow-300" data-testid="vs-map-detail-hq"><Home size={12} />HQ {Math.round(t.hq.x)}, {Math.round(t.hq.y)}</div>}
      {t.stash && <div className="text-xs flex items-center gap-1"><Package size={12} />Stash {Math.round(t.stash.x)}, {Math.round(t.stash.y)}</div>}
    </div>
  );
}

export function TerritoryMapView() {
  const [d] = useTerritoryMap();
  const [view, setView] = useState("map");
  const [sel, setSel] = useState(null);
  if (!d) return <Loading />;
  const t = d.territories.find((x) => x.id === sel);
  const contested = d.territories.filter((x) => x.contested).length;
  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-4" data-testid="vs-territory-map">
      <div className={`${glass} p-4`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-xs text-muted-foreground" data-testid="vs-map-stats">{d.territories.length} blocks · {d.territories.filter((x) => x.crew).length} held · <span className={contested ? "text-rose-400" : ""}>{contested} contested</span></div>
          <div className="flex bg-white/5 rounded-full p-1">
            {[["map", MapIcon, "City map"], ["grid", LayoutGrid, "Blocks"]].map(([k, I, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`vs-view-${k}`} className={`h-8 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${view === k ? "btn-gold text-black" : "hover:bg-white/10"}`}><I size={13} />{l}</button>))}
          </div>
        </div>
        {d.territories.length === 0 ? <Empty icon={MapPin} title="No territories yet" hint="City admins add blocks, or they sync from VirtuCity." />
          : view === "map" ? <CityMapCanvas map={d.map} territories={d.territories} selected={sel} onSelect={(x) => setSel(x.id)} />
          : <BlockGrid territories={d.territories} selected={sel} onSelect={(x) => setSel(x.id)} />}
      </div>
      <div className="space-y-4">
        <div className={`${glass} p-4`}><Detail t={t} /></div>
        <div className={`${glass} p-4`} data-testid="vs-map-legend">
          <div className="font-semibold text-sm mb-2">Crews on the map</div>
          {d.crews.length === 0 ? <p className="text-xs text-muted-foreground">No blocks held yet.</p> : d.crews.map((c) => (
            <Link key={c.id} to={`/vstreets/crew/${c.id}`} className="flex items-center gap-2 py-1 text-sm hover:text-gold" data-testid={`vs-legend-${c.id}`}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.color }} /><span className="flex-1 truncate">{c.name}</span>
              <span className="text-[11px] text-muted-foreground">{c.blocks}{c.contested ? ` · ${c.contested} at war` : ""}</span></Link>))}
          <div className="text-[10px] text-muted-foreground mt-3 space-y-0.5"><div><span className="text-rose-400">Red ring</span>: active turf war · dashed: frozen</div><div><span className="text-yellow-300">■</span> HQ and ● stash show only for your own crew</div></div>
        </div>
      </div>
    </div>
  );
}
